/**
 * The Kid Mode arc-reactor glyph — the centerpiece of Child's visual identity.
 * Same geometry as the operator's mockup (concentric cyan rings + pulsing core +
 * child-silhouette hint). Lives lower-right on every surface, tappable to talk.
 *
 * mode:
 *   idle      — slow breathing (SVG-animated, ~3s)
 *   listening — brighter, faster; core tracks mic amplitude (`level`)
 *   speaking  — outer ring + core pulse in sync with TTS amplitude (`level`)
 */
import { useRef } from "react";

type GlyphMode = "idle" | "listening" | "speaking" | "thinking" | "muted";

export default function Glyph({
  mode = "idle",
  size = 48,
  level = 0,
  onTap,
  onHold,
}: {
  mode?: GlyphMode;
  size?: number;
  level?: number; // 0..1 amplitude
  onTap?: () => void;
  onHold?: () => void; // press-and-hold (~600ms) — deliberate gesture, used for mute
}) {
  // Tap vs hold discrimination: a 7-year-old taps the glyph reflexively, so taps
  // must never do anything destructive; hold is the operator's gesture.
  const holdTimer = useRef<number | undefined>(undefined);
  const holdFired = useRef(false);
  const press = () => {
    holdFired.current = false;
    if (onHold) {
      holdTimer.current = window.setTimeout(() => {
        holdTimer.current = undefined;
        holdFired.current = true;
        onHold();
      }, 900);
    }
  };
  const release = () => {
    if (holdTimer.current) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = undefined;
    }
    if (!holdFired.current) onTap?.();
    holdFired.current = false;
  };
  const cancel = () => {
    if (holdTimer.current) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = undefined;
    }
    holdFired.current = false;
  };
  const active = mode === "listening" || mode === "speaking";
  // Kid-legible state colors: cyan = live, AMBER = working, grey+slash = mic off.
  // GOLD while it is HEARING YOU (matches the adult pad's operator-speaking gold),
  // cyan while JARVIS talks — so a talk-over is visible at a glance (operator 2026-07-11).
  // Refined same day: gold ONLY while the mic actually hears voice (with a short
  // hold so it doesn't flicker between words) — a connected-but-quiet glyph is
  // CYAN, not permanently gold ("stuck on yellow" report).
  const lastVoiceTs = useRef(0);
  if (mode === "listening" && level > 0.02) lastVoiceTs.current = Date.now();
  const hearing = mode === "listening" && Date.now() - lastVoiceTs.current < 900;
  const c = hearing ? "#ffd166" : mode === "thinking" ? "#ffb84d" : mode === "muted" ? "#5f6f78" : "var(--kid-cyan)";
  // Transmission indicator (operator spec): while ANYONE is talking the core
  // swells hard — up to ~4x its resting size, allowed to reach past the outer
  // ring — so you can SEE the channel is live even when the reply lags.
  const amp = Math.min(1, level * 5); // vu levels are small; gain them up
  const coreR = active ? 5 + amp * 17 : 5;
  const outerOpacity = active ? 0.9 : 0.8;
  const strokeW = mode === "speaking" ? 1.5 + amp * 1.2 : 1.5;
  const glow = active ? `drop-shadow(0 0 ${Math.round(amp * 12)}px ${c})` : "none";
  // Bao a une frimousse kawaii ; Isaac garde l'arc-reactor. (thème posé sur <html>)
  const isBao = typeof document !== "undefined" && document.documentElement.dataset.child === "bao";

  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      style={{ cursor: onTap || onHold ? "pointer" : "default", overflow: "visible", touchAction: "manipulation" }}
      role={onTap ? "button" : undefined}
      aria-label={onTap ? "Talk to JARVIS" : undefined}
    >
      {isBao ? (
        /* ── Frimousse kawaii de Bao (garde la réactivité d'état : couleur, halo, glow) ── */
        <>
          {/* halo doux qui gonfle quand ça écoute / parle */}
          <circle cx="24" cy="24" r={20 + amp * 6} fill="none" stroke={c} strokeWidth={strokeW} opacity={active ? 0.5 : 0.35}>
            {mode === "idle" && (
              <animate attributeName="opacity" values="0.25;0.5;0.25" dur="3s" repeatCount="indefinite" />
            )}
          </circle>
          <g style={{ transformOrigin: "24px 24px", transform: `scale(${active ? 1 + amp * 0.12 : 1})`, transition: "transform 90ms linear", filter: glow }}>
            {/* joue ronde qui respire */}
            <circle cx="24" cy="24" r="15" fill={c} opacity="0.16" stroke={c} strokeWidth="2">
              {mode === "idle" && (
                <animate attributeName="r" values="14.4;15.6;14.4" dur="2.6s" repeatCount="indefinite" />
              )}
            </circle>
            {/* joues roses */}
            <circle cx="15" cy="27" r="2.6" fill={c} opacity="0.32" />
            <circle cx="33" cy="27" r="2.6" fill={c} opacity="0.32" />
            {/* yeux */}
            <circle cx="19" cy="22" r="2.4" fill={c} />
            <circle cx="29" cy="22" r="2.4" fill={c} />
            {/* sourire — s'ouvre quand JARVIS parle */}
            <path
              d={mode === "speaking" ? "M 18 27 Q 24 33 30 27 Q 24 30 18 27 Z" : "M 18 27 Q 24 32 30 27"}
              fill={mode === "speaking" ? c : "none"}
              stroke={c}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </>
      ) : (
        <>
          {/* outer ring */}
          <circle cx="24" cy="24" r="20" fill="none" stroke={c} strokeWidth={strokeW} opacity={outerOpacity}>
            {mode === "idle" && (
              <animate attributeName="opacity" values="0.5;0.9;0.5" dur="3s" repeatCount="indefinite" />
            )}
          </circle>
          {/* middle ring */}
          <circle cx="24" cy="24" r="14" fill="none" stroke={c} strokeWidth="1" opacity="0.5">
            {mode === "idle" && (
              <animate attributeName="opacity" values="0.3;0.7;0.3" dur="3s" repeatCount="indefinite" />
            )}
          </circle>
          {/* pulsing core (arc reactor) */}
          <circle cx="24" cy="24" r={coreR} fill={c} opacity={active ? 1 : 0.9}
            style={{ transition: "r 90ms linear", filter: glow }}>
            {mode === "idle" && (
              <>
                <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />
              </>
            )}
          </circle>
          {/* working: rotating amber arc — unmistakable "JARVIS is on it" */}
          {mode === "thinking" && (
            <circle cx="24" cy="24" r="20" fill="none" stroke={c} strokeWidth="3" strokeDasharray="28 98" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate" from="0 24 24" to="360 24 24" dur="1.1s" repeatCount="indefinite" />
            </circle>
          )}
        </>
      )}
      {/* muted: hard red slash across the glyph */}
      {mode === "muted" && (
        <line x1="11" y1="37" x2="37" y2="11" stroke="#ff5a5a" strokeWidth="3.5" strokeLinecap="round" />
      )}
      {/* child-silhouette hint REMOVED 2026-07-11 — read as a stale ghost dot, not a silhouette (operator) */}
    </svg>
  );
}
